/**
 * Meta advanced matching: tells the Meta pixel (via GTM) who the signed-in
 * customer is, so browser events such as AddToCart match the same person as
 * the server-side Purchase that Shopify sends.
 *
 * This is the one place customer identity may enter the dataLayer. The GTM
 * "Meta - Advanced Matching" tag passes it to `fbq('init', …)`, and the pixel
 * script SHA-256 hashes every field before it leaves the browser. No GTM tag
 * routes the `user_data` event anywhere else (GA4 triggers on a fixed list of
 * funnel event names), so do not add fields here that only GA4 would see.
 *
 * Etsy accounts sign in on a synthetic address, so they are skipped entirely.
 *
 * `external_id` is the Shopify customer ID (numeric, as a string) because that
 * is the identifier the Shopify Meta app sends with Purchase; both sides must
 * use the same raw value for Meta to treat them as one person.
 */
import { isEtsyUser } from '../services/etsyService';

let lastPushedKey = null;

/** Test hook: forget what was pushed so the next call pushes again. */
export function resetMetaUserData() {
  lastPushedKey = null;
}

function clean(value) {
  return typeof value === 'string' ? value.trim().toLowerCase() : '';
}

export function shopifyCustomerIdFor(profile) {
  const linked = Array.isArray(profile?.socialAccounts)
    ? profile.socialAccounts.find((a) => a?.provider === 'shopify' && a?.providerId)
    : null;
  const id = linked?.providerId || safeLocalStorageGet('shopifyCustomerId');
  const str = id == null ? '' : String(id).trim();
  return /^\d+$/.test(str) ? str : '';
}

function safeLocalStorageGet(key) {
  try {
    return localStorage.getItem(key);
  } catch (err) {
    return null;
  }
}

/**
 * @param {object|null} profile state.profile.data
 * @returns {boolean} true when a `user_data` event was pushed
 */
export function pushMetaUserData(profile) {
  try {
    if (!profile || isEtsyUser(profile)) return false;

    const em = clean(profile.email);
    if (!em || !em.includes('@')) return false;

    const userData = { em };
    const fn = clean(profile.firstName);
    const ln = clean(profile.lastName);
    const externalId = shopifyCustomerIdFor(profile);
    if (fn) userData.fn = fn;
    if (ln) userData.ln = ln;
    if (externalId) userData.external_id = externalId;

    const key = JSON.stringify(userData);
    if (key === lastPushedKey) return false;

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event: 'user_data', user_data: userData });
    lastPushedKey = key;
    return true;
  } catch (err) {
    console.warn('[GTM] user_data push failed (non-blocking)');
    return false;
  }
}
